import { motion, AnimatePresence } from 'framer-motion';
import { OptimizerMoreMenu } from '../OptimizerMoreMenu';
import { useTranslation } from 'react-i18next';
import {
  Globe,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  FileText,
  Link2,
  ClipboardList,
  Sparkles,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

import { useJobScraping } from '@soVendor/hooks/useJobScraping';
import { JobData } from '../shared/types';
import { STEP_CARD_COMPACT_CLASS, STEP_HEADER_ICON_CLASS, STEP_PRIMARY_CTA_CLASS } from '../shared/stepLayout';
const JOB_POST_MAX = 6000;

type ActiveTab = 'url' | 'description';

function jobPostTextFromJobData(job: JobData): string {
  if (job.description?.trim()) return job.description.trim();
  const parts: string[] = [];
  const req = job.requirements ?? [];
  if (req.length) parts.push('Requirements:\n' + req.map((r) => `• ${r}`).join('\n'));
  const skills = job.skills ?? job.required_skills ?? [];
  if (skills.length) parts.push('Skills:\n' + skills.map((s) => `• ${s}`).join('\n'));
  return parts.join('\n\n') || '';
}

interface JobUrlStepProps {
  onNext: (data: JobData) => void;
  onBack: () => void;
  jobData?: JobData;
  onJobParse: (url: string) => Promise<JobData>;
  isLoading: boolean;
  error: string | null;
}

const JobUrlStep: React.FC<JobUrlStepProps> = ({
  onNext,
  onBack,
  jobData: initialJobData,
  onJobParse,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const jobHook = useJobScraping();

  const [activeTab, setActiveTab] = useState<ActiveTab>('url');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobPost, setJobPost] = useState('');

  const jobUrl = jobHook.jobUrls[0];
  const jobData = jobHook.jobDataArray[0];

  // Restore from initialJobData (navigating back)
  useEffect(() => {
    if (initialJobData && !jobData?.data) {
      jobHook.updateJobUrl(0, initialJobData.url ?? '');
      if (!jobHook.jobDataArray[0]?.data) {
        jobHook.jobDataArray[0] = { status: 'success', data: initialJobData, error: null };
      }
      if (!jobHook.jobUrls[0]?.url || jobHook.jobUrls[0].status !== 'success') {
        jobHook.jobUrls[0] = { url: initialJobData.url ?? '', status: 'success', error: null };
      }
      setJobTitle(initialJobData.title || '');
      setCompanyName(initialJobData.company || '');
      setJobPost(jobPostTextFromJobData(initialJobData));
    }
  }, [initialJobData, jobData, jobHook]);

  // Auto-fill from fetched job data
  useEffect(() => {
    if (jobData?.data) {
      setJobTitle(jobData.data.title || '');
      setCompanyName(jobData.data.company || '');
      setJobPost(jobPostTextFromJobData(jobData.data));
    }
  }, [jobData?.data]);

  const FETCH_JOB_TIMEOUT_MS = 60_000;

  const handleFetchJob = async () => {
    if (!jobUrl?.url?.trim()) return;
    setLocalError(null);
    setIsProcessing(true);
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(t('optimizer.jobUrlStep.failedToFetch'))), FETCH_JOB_TIMEOUT_MS)
    );
    try {
      await Promise.race([jobHook.handleJobScraping(), timeoutPromise]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('optimizer.jobUrlStep.failedToFetch');
      setLocalError(
        msg === 'GUEST_AUTH_REQUIRED' ? t('optimizer.jobUrlStep.signInToFetch') : msg
      );
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => () => setIsProcessing(false), []);

  const jobPostLength = jobPost.length;
  const hasFetchedData = !!(jobData?.data);
  const descriptionReady = jobPost.trim().length >= 50;
  const canContinue = hasFetchedData || descriptionReady;

  const handleNextClick = async () => {
    setLocalError(null);
    try {
      if (hasFetchedData) {
        onNext(jobData!.data!);
        return;
      }
      if (descriptionReady) {
        const descriptionText = [
          jobTitle.trim() ? `Job title: ${jobTitle.trim()}` : '',
          companyName.trim() ? `Company: ${companyName.trim()}` : '',
          jobPost.trim(),
        ]
          .filter(Boolean)
          .join('\n\n');
        const parsed = await onJobParse(descriptionText);
        onNext(parsed);
        return;
      }
      setLocalError(t('optimizer.jobUrlStep.pasteOrUrl'));
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : t('optimizer.jobUrlStep.failedToProcess')
      );
    }
  };

  const displayError = jobUrl?.error || localError || error;

  // ── Shared fields: Job title + Company name ──────────────────────────────────
  // IMPORTANT: this is a render function (not a JSX variable) so each tab gets its
  // own independent element. Storing JSX in a variable and using it in two places
  // inside AnimatePresence causes a "removeChild" crash because AnimatePresence
  // briefly mounts both children simultaneously and React sees the same node twice.
  const renderSharedFields = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
      <div className="min-w-0">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
          {t('optimizer.jobUrlStep.jobTitle')}
        </label>
        <input
          type="text"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          placeholder={t('optimizer.jobUrlStep.jobTitlePlaceholder')}
          className="w-full px-4 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] focus:border-transparent touch-manipulation text-sm"
          disabled={isProcessing || isLoading}
        />
      </div>
      <div className="min-w-0">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
          {t('optimizer.jobUrlStep.companyName')}
        </label>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder={t('optimizer.jobUrlStep.companyPlaceholder')}
          className="w-full px-4 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] focus:border-transparent touch-manipulation text-sm"
          disabled={isProcessing || isLoading}
        />
      </div>
    </div>
  );

  /* ─────────────────────────────────────────────────────────────────────────
     NATIVE MOBILE RENDER — full-screen splash with tabs in header
     ───────────────────────────────────────────────────────────────────────── */
  if (isNative) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className=""
        style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const }}
      >
        {/* ── Gradient header with back + title + segmented tabs ── */}
        <div
          className="flex-shrink-0"
          style={{
            background: 'var(--ggj-header-bg)',
            borderRadius: '0 0 24px 24px',
            boxShadow: 'var(--brand-cta-shadow, 0 4px 20px rgba(108,71,255,0.30))',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
            paddingBottom: 0,
          }}
        >
          {/* Top row */}
          <div className="flex items-center justify-between px-4 pb-3">
            <button
              type="button"
              onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation" style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-btn-icon)' }}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center">
              <span className="text-[10px] font-semibold uppercase tracking-widest leading-none" style={{ color: 'var(--ggj-header-sub)' }}>Resume Optimizer</span>
              <span className="text-base font-bold leading-tight mt-0.5" style={{ color: 'var(--ggj-header-text)' }}>Add Job Details</span>
            </div>
            <div className="w-9 h-9" />
          </div>

          {/* Segmented tab control — inside header */}
          <div className="flex mx-4 mb-4 gap-1 p-1 bg-white/10 rounded-2xl">
            <button
              type="button"
              onClick={() => { setActiveTab('url'); setLocalError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all touch-manipulation ${
                activeTab === 'url'
                  ? 'bg-white text-[#6C47FF] shadow-md'
                  : 'text-white/70'
              }`}
            >
              <Link2 className="w-3.5 h-3.5 shrink-0" />
              [Job URL]
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('description'); setLocalError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all touch-manipulation ${
                activeTab === 'description'
                  ? 'bg-white text-[#6C47FF] shadow-md'
                  : 'text-white/70'
              }`}
            >
              <ClipboardList className="w-3.5 h-3.5 shrink-0" />
              [Paste JD]
            </button>
          </div>
        </div>

        {/* ── Scrollable content (z-index 0 so sticky bar stays on top) ── */}
        <div className="vscode-bg-secondary flex min-h-0 flex-1 flex-col overflow-y-auto bg-white" style={{ position: 'relative' as const, zIndex: 0 }}>
          <div className="flex-1 px-4 pt-5 pb-4 flex flex-col gap-4">
            <AnimatePresence mode="wait" initial={false}>
              {activeTab === 'url' ? (
                <motion.div
                  key="native-url"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col gap-4"
                >
                  {/* URL input block */}
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 overflow-hidden">
                    <div className="px-4 pt-4 pb-3">
                      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-2">
                        Paste job post URL
                      </label>
                      <div className="flex items-center gap-2 bg-white dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2.5">
                        <Globe className="w-4 h-4 vscode-text-tertiary shrink-0" />
                        <input
                          type="url"
                          value={jobUrl?.url ?? ''}
                          onChange={(e) => { jobHook.updateJobUrl(0, e.target.value); setLocalError(null); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleFetchJob(); }}
                          placeholder="https://www.linkedin.com/jobs/view/..."
                          className="flex-1 min-w-0 text-sm bg-transparent text-gray-900 dark:text-white placeholder-gray-400 outline-none min-h-[28px] touch-manipulation"
                          disabled={isProcessing || isLoading}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleFetchJob}
                      disabled={!jobUrl?.url?.trim() || isProcessing || isLoading || jobUrl?.status === 'scraping'}
                      className="w-full flex items-center justify-center gap-2 py-3.5 text-sm font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                      style={{ background: 'var(--ggj-btn-bg)', border: '1px solid var(--ggj-btn-border)', color: 'var(--ggj-btn-text)', boxShadow: 'var(--ggj-btn-shadow)' }}
                    >
                      {isProcessing || jobUrl?.status === 'scraping' ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> [Fetching job details…]</>
                      ) : (
                        <><Globe className="w-4 h-4" /> [Fetch Job Details]</>
                      )}
                    </button>
                  </div>

                  {/* Success state — show fetched info */}
                  {jobUrl?.status === 'success' && hasFetchedData && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                        <span className="text-sm font-bold text-green-700 dark:text-green-300">Job details fetched</span>
                      </div>
                      {(jobTitle || companyName) && (
                        <div className="space-y-1">
                          {jobTitle && <p className="text-sm font-semibold text-gray-900 dark:text-white">{jobTitle}</p>}
                          {companyName && <p className="text-xs" style={{ color: 'var(--ggj-text-muted)' }}>{companyName}</p>}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Job description preview / edit */}
                  <div>
                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-2">
                      Job Description {hasFetchedData ? <span className="text-green-600 font-normal normal-case">— auto-filled, editable</span> : ''}
                    </label>
                    <div className="relative">
                      <textarea
                        value={jobPost}
                        onChange={(e) => { setJobPost(e.target.value.slice(0, JOB_POST_MAX)); setLocalError(null); }}
                        placeholder="Job description will appear here after fetching, or paste manually..."
                        maxLength={JOB_POST_MAX}
                        rows={7}
                        className="w-full px-4 py-3 pr-16 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] text-sm bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 resize-none touch-manipulation"
                        disabled={isProcessing || isLoading}
                      />
                      <span className="absolute bottom-3 right-3 text-[10px] vscode-text-tertiary tabular-nums pointer-events-none">{jobPost.length}/{JOB_POST_MAX}</span>
                    </div>
                    {jobPost.trim().length > 0 && jobPost.trim().length < 50 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 shrink-0" /> Add at least 50 characters
                      </p>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="native-desc"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col gap-4"
                >
                  {/* Hint banner */}
                  <div className="rounded-2xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 px-4 py-3 flex items-start gap-3">
                    <Sparkles className="w-4 h-4 text-[#6C47FF] shrink-0 mt-0.5" />
                    <p className="text-xs text-purple-700 dark:text-purple-300 leading-relaxed">
                      Copy the full job description from the job post and paste it below. Include responsibilities, requirements, and skills.
                    </p>
                  </div>

                  {/* Job Title */}
                  <div>
                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-2">Job Title</label>
                    <input
                      type="text"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="e.g. Senior Software Engineer"
                      className="w-full px-4 py-3 min-h-[48px] bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] touch-manipulation text-sm"
                      disabled={isProcessing || isLoading}
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-2">Company</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g. Acme Inc."
                      className="w-full px-4 py-3 min-h-[48px] bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] touch-manipulation text-sm"
                      disabled={isProcessing || isLoading}
                    />
                  </div>

                  {/* Job Description */}
                  <div>
                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-2">Full Job Description</label>
                    <div className="relative">
                      <textarea
                        value={jobPost}
                        onChange={(e) => { setJobPost(e.target.value.slice(0, JOB_POST_MAX)); setLocalError(null); }}
                        placeholder="Paste the full job description here (responsibilities, requirements, skills)..."
                        maxLength={JOB_POST_MAX}
                        rows={10}
                        className="w-full px-4 py-3 pr-16 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] resize-none touch-manipulation text-sm"
                        disabled={isProcessing || isLoading}
                      />
                      <span className="absolute bottom-3 right-3 text-[10px] vscode-text-tertiary tabular-nums pointer-events-none">{jobPostLength}/{JOB_POST_MAX}</span>
                    </div>
                    {jobPost.trim().length > 0 && jobPost.trim().length < 50 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 shrink-0" /> Add at least 50 characters
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error */}
            {displayError && (
              <div className="flex items-start gap-2 p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{displayError}</span>
              </div>
            )}
          </div>

          {/* ── Fixed bottom action: always on top of scroll content ── */}
          <div
            className="flex-shrink-0 px-4 pt-3 border-t flex items-stretch gap-2"
            style={{
              position: 'relative' as const,
              zIndex: 200,
              background: 'var(--ggj-nav-bg)',
              borderColor: 'var(--ggj-border)',
              paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
            }}
          >
            <button
              type="button"
              onClick={handleNextClick}
              disabled={!canContinue || isProcessing || isLoading}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-bold text-white transition-opacity disabled:opacity-50 touch-manipulation"
              style={{ background: 'var(--ggj-btn-bg)', border: '1px solid var(--ggj-btn-border)', color: 'var(--ggj-btn-text)', boxShadow: 'var(--ggj-btn-shadow)' }}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              [Continue to Analysis]
              <ArrowRight className="w-4 h-4 shrink-0" />
            </button>
            <OptimizerMoreMenu buttonStyle={{ minWidth: 52 }} />
          </div>
        </div>
      </motion.div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     WEB RENDER (unchanged)
     ───────────────────────────────────────────────────────────────────────── */
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col min-h-0 flex-1"
    >
      <div className="w-full min-w-0 max-w-full lg:max-w-5xl lg:mx-auto px-3 xs:px-4 sm:px-5 lg:px-8 flex flex-col gap-0 pb-4 py-2 sm:py-3">
        <div className="w-full">
          <div className={STEP_CARD_COMPACT_CLASS}>
            <div className="grid grid-cols-1 lg:grid-cols-[1.75fr_0.85fr] gap-0 min-w-0 w-full">
              <div className="p-3 sm:p-6 lg:p-10 flex flex-col min-w-0 order-1 w-full">
                <div className="sticky top-0 z-10 -mx-3 -mt-3 border-b vscode-border bg-gray-200/95 px-3 pb-4 pt-3 backdrop-blur-sm dark:bg-gray-900/95 sm:-mx-6 sm:px-6 sm:-mt-6 sm:pt-6 lg:-mx-10 lg:px-10 lg:-mt-10 lg:pt-10">
                  <div className="flex items-start gap-2.5">
                    <div className={`${STEP_HEADER_ICON_CLASS} h-8 w-8 rounded-lg`}>
                      <Sparkles className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold vscode-text-primary mb-0.5">{t('optimizer.jobUrlStep.title')}</h2>
                      <p className="text-xs vscode-text-secondary max-w-full line-clamp-2">{t('optimizer.jobUrlStep.description')}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-1 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-xl w-full sm:w-auto sm:inline-flex">
                    <button type="button" onClick={() => { setActiveTab('url'); setLocalError(null); }}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all min-h-[36px] touch-manipulation ${activeTab === 'url' ? 'bg-white dark:bg-gray-800 text-[#6C47FF] shadow-sm' : 'vscode-text-secondary'}`}>
                      <Link2 className="w-3.5 h-3.5 shrink-0" />[{t('optimizer.jobUrlStep.tabUrl')}]
                    </button>
                    <button type="button" onClick={() => { setActiveTab('description'); setLocalError(null); }}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all min-h-[36px] touch-manipulation ${activeTab === 'description' ? 'bg-white dark:bg-gray-800 text-[#6C47FF] shadow-sm' : 'vscode-text-secondary'}`}>
                      <ClipboardList className="w-3.5 h-3.5 shrink-0" />[{t('optimizer.jobUrlStep.tabDescription')}]
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 flex flex-col">
                  <AnimatePresence mode="wait" initial={false}>
                    {activeTab === 'url' ? (
                      <motion.div key="tab-url" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.18 }} className="flex flex-col gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">{t('optimizer.jobUrlStep.pasteUrlLabel')}</label>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex-1 min-w-0 relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Globe className="w-4 h-4 vscode-text-tertiary shrink-0" /></div>
                              <input type="url" value={jobUrl?.url ?? ''} onChange={(e) => { jobHook.updateJobUrl(0, e.target.value); setLocalError(null); }} onKeyDown={(e) => { if (e.key === 'Enter') handleFetchJob(); }} placeholder={t('optimizer.jobUrlStep.urlPlaceholder')} className="w-full pl-9 pr-3 py-3 text-sm bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] disabled:opacity-60 min-h-[44px] touch-manipulation" disabled={isProcessing || isLoading} />
                            </div>
                            <button type="button" onClick={handleFetchJob} disabled={!jobUrl?.url?.trim() || isProcessing || isLoading || jobUrl?.status === 'scraping'} className={`flex min-h-[44px] shrink-0 touch-manipulation items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold ${STEP_PRIMARY_CTA_CLASS}`}>
                              {isProcessing || jobUrl?.status === 'scraping' ? <><Loader2 className="w-4 h-4 animate-spin" /><span className="hidden sm:inline">[{t('optimizer.jobUrlStep.fetchingMessage')}]</span></> : <><Globe className="w-4 h-4" />[{t('optimizer.jobUrlStep.fetch')}]</>}
                            </button>
                          </div>
                          {jobUrl?.status === 'success' && <p className="text-xs vscode-text-secondary mt-1.5 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 shrink-0 text-[#6C47FF]" />{t('optimizer.jobUrlStep.fetchedMessage')}</p>}
                        </div>
                        {renderSharedFields()}
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">{t('optimizer.jobUrlStep.jobPost')}</label>
                          <div className="relative">
                            <textarea value={jobPost} onChange={(e) => { setJobPost(e.target.value.slice(0, JOB_POST_MAX)); setLocalError(null); }} placeholder={t('optimizer.jobUrlStep.jobPostPlaceholder')} maxLength={JOB_POST_MAX} rows={6} className="w-full min-h-[140px] px-4 py-3 pr-20 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] resize-y touch-manipulation text-sm bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600" disabled={isProcessing || isLoading} />
                            <span className="absolute bottom-3 right-3 text-xs vscode-text-tertiary tabular-nums pointer-events-none">{jobPost.length}/{JOB_POST_MAX}</span>
                          </div>
                          {jobPost.trim().length > 0 && jobPost.trim().length < 50 && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" />{t('optimizer.jobUrlStep.minChars')}</p>}
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div key="tab-desc" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.18 }} className="flex flex-col gap-4">
                        {renderSharedFields()}
                        <div className="flex flex-col flex-1 min-h-0">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">{t('optimizer.jobUrlStep.jobPost')}</label>
                          <div className="relative flex-1 min-h-[200px]">
                            <textarea value={jobPost} onChange={(e) => { setJobPost(e.target.value.slice(0, JOB_POST_MAX)); setLocalError(null); }} placeholder={t('optimizer.jobUrlStep.jobPostPlaceholder')} maxLength={JOB_POST_MAX} rows={8} className="w-full min-h-[200px] px-4 py-3 pr-20 bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] resize-y touch-manipulation text-sm" disabled={isProcessing || isLoading} />
                            <span className="absolute bottom-3 right-3 text-xs vscode-text-tertiary tabular-nums pointer-events-none">{jobPostLength}/{JOB_POST_MAX}</span>
                          </div>
                          {jobPost.trim().length > 0 && jobPost.trim().length < 50 && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" />{t('optimizer.jobUrlStep.minChars')}</p>}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {displayError && <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200 mt-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{displayError}</span></div>}
                <div className="mt-6 flex justify-end">
                  <button type="button" onClick={handleNextClick} disabled={!canContinue || isProcessing || isLoading} aria-label={t('optimizer.jobUrlStep.continueToPreview')} className={`flex touch-manipulation items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-opacity ${canContinue && !isProcessing && !isLoading ? STEP_PRIMARY_CTA_CLASS : 'cursor-not-allowed bg-gray-300 text-gray-500 opacity-70 dark:bg-gray-600'}`}>
                    {t('optimizer.jobUrlStep.continueToPreview')}<ArrowRight className="w-4 h-4 shrink-0" />
                  </button>
                </div>
              </div>
              <div className="p-3 sm:p-6 lg:p-8 xl:p-10 vscode-bg-secondary border-t lg:border-t-0 lg:border-l vscode-border flex flex-col justify-center min-h-0 lg:min-h-[260px] min-w-0 w-full order-2">
                <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-[#6C47FF] dark:text-[#5B8DFF] mb-2 sm:mb-4">{t('optimizer.jobUrlStep.howItWorks')}</h3>
                <div className="space-y-2 sm:space-y-3">
                  <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-3 sm:p-4 min-w-0">
                    <h4 className="vscode-text-primary mb-1.5 flex items-center gap-2 text-xs font-medium sm:text-sm"><CheckCircle className="w-3.5 h-3.5 shrink-0 text-[#6C47FF] sm:h-4 sm:w-4" />{t('optimizer.jobUrlStep.whatWeUse')}</h4>
                    <ul className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm vscode-text-secondary">
                      <li>• {t('optimizer.jobUrlStep.whatWeUseBullet1')}</li><li>• {t('optimizer.jobUrlStep.whatWeUseBullet2')}</li><li>• {t('optimizer.jobUrlStep.whatWeUseBullet3')}</li><li>• {t('optimizer.jobUrlStep.whatWeUseBullet4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(JobUrlStep);
